import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { format, subDays, startOfDay, endOfDay } from "date-fns";
import { analyticsAPI, createWebSocket } from "../api";
import {
  ComposedChart, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, AreaChart, Area
} from "recharts";
import {
  MessageSquare, Users, Clock, TrendingUp, TrendingDown,
  AlertTriangle, CheckCircle, Smile, Frown
} from "lucide-react";
import { motion } from "framer-motion";
import DateRangePicker from "../components/DateRangePicker";

const COLORS = ["#1e3a8a", "#14b8a6", "#f59e0b", "#e11d48", "#0f766e", "#94a3b8"];
const DASHBOARD_STORAGE_KEY = "dashboard_preset_days";

function getInitialDateRange() {
  let days = 7;
  try {
    const saved = localStorage.getItem(DASHBOARD_STORAGE_KEY);
    if (saved !== null) {
      const d = parseInt(saved, 10);
      if (!isNaN(d) && d >= 0) days = d;
    }
  } catch { }
  const end = new Date();
  const start = days === 0 ? startOfDay(end) : startOfDay(subDays(end, days));
  return { startDate: start, endDate: endOfDay(end) };
}

function MetricCard({ title, value, change, icon: Icon, color, subtitle }) {
  const isPositive = change >= 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card hover:shadow-md transition-shadow border-l-4"
      style={{ borderLeftColor: '#14b8a6' }}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold text-blue-900/60 uppercase tracking-wider">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
        <div className={`p-2.5 rounded-xl`} style={{ background: 'linear-gradient(135deg, #1e3a8a, #14b8a6)' }}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      {change !== undefined && change !== null && (
        <div className={`flex items-center mt-3 text-sm ${isPositive ? "text-teal-600" : "text-red-600"}`}>
          {isPositive ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
          <span className="font-medium">{isPositive ? "+" : ""}{change}%</span>
          <span className="text-gray-400 ml-1">vs tuần trước</span>
        </div>
      )}
    </motion.div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(getInitialDateRange);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        date_from: format(dateRange.startDate, "yyyy-MM-dd'T'HH:mm:ss"),
        date_to: format(dateRange.endDate, "yyyy-MM-dd'T'HH:mm:ss"),
      };
      const res = await analyticsAPI.dashboard(params);
      setData(res.data);
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // WebSocket for real-time updates
  useEffect(() => {
    let ws;
    let cancelled = false;
    const connectTimeout = setTimeout(() => {
      if (cancelled) return;
      try {
        ws = createWebSocket("dashboard");
        if (!ws) return;
        ws.onmessage = (event) => {
          try {
            const update = JSON.parse(event.data);
            if (update.type === "dashboard_update") {
              setData((prev) => ({ ...prev, ...update.data }));
            }
          } catch { }
        };
        ws.onerror = () => { };
        ws.onclose = () => { };
      } catch { }
    }, 300); // small delay to survive StrictMode double-mount
    return () => {
      cancelled = true;
      clearTimeout(connectTimeout);
      if (ws && ws.readyState <= 1) ws.close();
    };
  }, []);

  const summary = data?.summary || {};
  const dailyTrend = data?.daily_trend || [];

  const TrendTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    let dateStr = label;
    try { const d = new Date(label); dateStr = `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`; } catch { }
    const labelMap = {
      conversations: "Hội thoại",
      resolved: "Đã giải quyết",
      new_customers: "Khách hàng mới",
      resolution_rate: "Tỷ lệ giải quyết",
    };
    return (
      <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 4px 20px rgba(0,0,0,0.1)", padding: "10px 14px", fontSize: 12 }}>
        <p style={{ fontWeight: 600, color: "#374151", marginBottom: 6 }}>{dateStr}</p>
        {payload.map((e, i) => (
          <p key={i} style={{ color: e.color, margin: "2px 0", display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: e.color }} />
            {labelMap[e.dataKey] || e.name}:{" "}
            <span style={{ fontWeight: 600 }}>
              {e.value != null ? e.value : "—"}{e.dataKey === "resolution_rate" ? "%" : ""}
            </span>
          </p>
        ))}
      </div>
    );
  };

  const AvgRtTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    let dateStr = label;
    try { const d = new Date(label); dateStr = `${d.getDate()}/${d.getMonth() + 1}`; } catch { }
    return (
      <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 4px 20px rgba(0,0,0,0.1)", padding: "8px 12px", fontSize: 12 }}>
        <p style={{ fontWeight: 600, color: "#374151", marginBottom: 4 }}>{dateStr}</p>
        {payload[0] && <p style={{ color: payload[0].color }}>
          TG phản hồi: <strong>{payload[0].value != null ? `${payload[0].value} ph` : "—"}</strong>
        </p>}
      </div>
    );
  };
  const sentimentDist = data?.sentiment_distribution || [];
  const topTopics = data?.top_topics || [];
  const recentAlerts = data?.recent_alerts || [];

  return (
    <div className="space-y-6">
      {/* Header — always rendered so DateRangePicker is never unmounted */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-2xl font-bold text-gray-900">Dashboard</h3>
          <p className="text-gray-500 text-sm mt-1">Tổng quan hiệu suất CSKH qua Zalo OA</p>
        </div>
        <DateRangePicker
          initialDays={7}
          storageKey={DASHBOARD_STORAGE_KEY}
          onChange={({ startDate, endDate }) => setDateRange({ startDate, endDate })}
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <>
          {/* Metric Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Tổng hội thoại"
              value={summary.total_conversations?.toLocaleString() || "0"}
              change={summary.conv_change}
              icon={MessageSquare}
              color="bg-primary-500"
            />
            <MetricCard
              title="Khách hàng mới"
              value={summary.new_customers?.toLocaleString() || "0"}
              change={summary.customer_change}
              icon={Users}
              color="bg-green-500"
            />
            <MetricCard
              title="Thời gian PH trung bình"
              value={summary.avg_response_time || "N/A"}
              change={summary.response_time_change}
              icon={Clock}
              color="bg-amber-500"
              subtitle="phút"
            />
            <MetricCard
              title="Tỷ lệ giải quyết"
              value={`${summary.resolution_rate || 0}%`}
              change={summary.resolution_change}
              icon={CheckCircle}
              color="bg-purple-500"
            />
          </div>

          {/* Charts Row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Trend Chart */}
            <div className="card lg:col-span-2">
              <h4 className="text-base font-semibold text-gray-900 mb-4">Xu hướng hội thoại</h4>
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={dailyTrend}>
                  <defs>
                    <linearGradient id="colorConv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1e3a8a" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#1e3a8a" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8"
                    tickFormatter={(v) => { try { const d = new Date(v); return `${d.getDate()}/${d.getMonth() + 1}`; } catch { return v; } }}
                  />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip content={<TrendTooltip />} />
                  <Legend
                    formatter={(value) => ({
                      conversations: "Hội thoại",
                      resolved: "Đã giải quyết",
                      new_customers: "KH mới",
                    }[value] || value)}
                    wrapperStyle={{ fontSize: 11 }}
                  />
                  <Area type="monotone" dataKey="conversations" name="conversations" stroke="#1e3a8a" fill="url(#colorConv)" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="resolved" name="resolved" stroke="#14b8a6" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="new_customers" name="new_customers" stroke="#f59e0b" strokeWidth={2} dot={false} strokeDasharray="5 3" />
                </ComposedChart>
              </ResponsiveContainer>

              {/* Mini avg response time chart */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs font-medium text-gray-500 mb-2">Thời gian phản hồi trung bình (phút/ngày)</p>
                <ResponsiveContainer width="100%" height={120}>
                  <ComposedChart data={dailyTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#94a3b8"
                      tickFormatter={(v) => { try { const d = new Date(v); return `${d.getDate()}/${d.getMonth() + 1}`; } catch { return v; } }}
                    />
                    <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" unit=" ph" width={45} />
                    <Tooltip content={<AvgRtTooltip />} />
                    <Area type="monotone" dataKey="avg_rt_min" stroke="#0f766e" fill="#f0fdfa"
                      strokeWidth={2} dot={false} connectNulls />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Sentiment Distribution — Click để drill-down */}
            <div className="card">
              <h4 className="text-base font-semibold text-gray-900 mb-4">Phân bố cảm xúc</h4>
              <p className="text-xs text-gray-400 -mt-2 mb-3">Nhấp vào phần để xem hội thoại theo cảm xúc</p>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={sentimentDist.length > 0 ? sentimentDist : [
                      { name: "Tích cực", value: 45 },
                      { name: "Trung lập", value: 35 },
                      { name: "Tiêu cực", value: 20 },
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={5}
                    dataKey="value"
                    style={{ cursor: "pointer" }}
                    onClick={(data) => {
                      const sentimentMap = { "Tích cực": "positive", "Trung lập": "neutral", "Tiêu cực": "negative" };
                      const sentiment = sentimentMap[data?.name];
                      if (sentiment) navigate(`/conversations?sentiment=${sentiment}`);
                    }}
                  >
                    {(sentimentDist.length > 0 ? sentimentDist : [1, 2, 3]).map((_, i) => (
                      <Cell key={i} fill={["#14b8a6", "#f59e0b", "#e11d48"][i] || COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={36}
                    onClick={(e) => {
                      const sentimentMap = { "Tích cực": "positive", "Trung lập": "neutral", "Tiêu cực": "negative" };
                      const sentiment = sentimentMap[e?.value];
                      if (sentiment) navigate(`/conversations?sentiment=${sentiment}`);
                    }}
                    wrapperStyle={{ cursor: "pointer" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Charts Row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Topics — Click để drill-down */}
            <div className="card">
              <h4 className="text-base font-semibold text-gray-900 mb-4">Chủ đề phổ biến</h4>
              <p className="text-xs text-gray-400 -mt-2 mb-3">Nhấp vào chủ đề để xem danh sách hội thoại</p>
              {topTopics.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={topTopics}
                    layout="vertical"
                    onClick={(chartData) => {
                      if (chartData && chartData.activePayload && chartData.activePayload.length > 0) {
                        const topic = chartData.activePayload[0].payload.topic;
                        navigate(`/conversations?topic=${encodeURIComponent(topic)}`);
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <YAxis dataKey="topic" type="category" tick={{ fontSize: 11 }} stroke="#94a3b8" width={100} />
                    <Tooltip
                      contentStyle={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}
                      formatter={(value, name) => [value, "Số cuộc hội thoại"]}
                    />
                    <Bar dataKey="count" fill="#1e3a8a" radius={[0, 6, 6, 0]}
                      className="cursor-pointer"
                      onMouseEnter={(data, index, e) => { if (e && e.target) e.target.style.opacity = 0.8; }}
                      onMouseLeave={(data, index, e) => { if (e && e.target) e.target.style.opacity = 1; }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[300px] text-gray-400 text-sm">
                  Chưa có dữ liệu chủ đề trong khoảng thời gian này
                </div>
              )}
            </div>

            {/* Recent Alerts */}
            <div className="card">
              <h4 className="text-base font-semibold text-gray-900 mb-4">Cảnh báo gần đây</h4>
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {(recentAlerts.length > 0 ? recentAlerts : [
                  { type: "complaint", message: "Tăng đột biến khiếu nại về vận chuyển", time: "2 giờ trước", severity: "high" },
                  { type: "churn", message: "3 khách hàng VIP có nguy cơ rời bỏ cao", time: "4 giờ trước", severity: "medium" },
                  { type: "performance", message: "Thời gian phản hồi tăng 25% so với hôm qua", time: "6 giờ trước", severity: "low" },
                ]).map((alert, i) => (
                  <div
                    key={i}
                    className={`flex items-start space-x-3 p-3 rounded-lg border ${(alert.severity === "high" || alert.type === "danger")
                      ? "bg-red-50 border-red-200"
                      : (alert.severity === "medium" || alert.type === "warning")
                        ? "bg-amber-50 border-amber-200"
                        : "bg-blue-50 border-blue-200"
                      }`}
                  >
                    <AlertTriangle
                      className={`w-4 h-4 mt-0.5 flex-shrink-0 ${(alert.severity === "high" || alert.type === "danger") ? "text-red-500" : (alert.severity === "medium" || alert.type === "warning") ? "text-amber-500" : "text-blue-500"
                        }`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800">{alert.message}</p>
                      <p className="text-xs text-gray-400 mt-1">{alert.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
